// src/pages/ParentProfile.js (v4 – config-driven, clean)

import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import api from "../services/api";
import { API_PATHS } from "../config";
import "../styles/ParentProfile.css";

/**
 * Profile page for parent users.
 *
 * Displays school info and allows editing display name.
 *
 * @component
 * @returns {JSX.Element}
 */
const ParentProfile = () => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editing state
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Load profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.get(API_PATHS.PROFILE);
        setProfile(data);
        setNewDisplayName(data.display_name || "");
      } catch (err) {
        console.error("Failed to load profile:", err);
        setError("Failed to load your profile. Please refresh.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleUpdateDisplayName = async () => {
    const trimmed = newDisplayName.trim();
    if (!trimmed) {
      setError("Display name cannot be empty.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.post(API_PATHS.UPDATE_DISPLAY_NAME, {
        new_display_name: trimmed,
      });

      setProfile((prev) => ({ ...prev, display_name: trimmed }));
      setEditingDisplayName(false);
      setMessage("Display name updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update display name.");
    } finally {
      setSaving(false);
    }
  };

  const emailPrefix = profile.email ? profile.email.split("@")[0] : "";
  const showNudge = profile.display_name === emailPrefix;

  if (loading) {
    return (
      <div className="parent-profile-container page">
        <div className="parent-profile-card parent-profile-card--loading">
          <p className="parent-profile-meta">Loading your profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-profile-container page">
      <header className="parent-profile-header">
        <h1 className="parent-profile-title">Parent profile</h1>
        <p className="parent-profile-subtitle">
          Manage how your name appears to your school and other parents.
        </p>
      </header>

      {message && <p className="success-text">{message}</p>}
      {error && <p className="error-text">{error}</p>}

      <section className="parent-profile-card">
        <div className="parent-profile-grid">
          {/* Display name */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">Display name</span>
            <div className="parent-profile-value parent-profile-value--stack">
              {editingDisplayName ? (
                <>
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    maxLength={255}
                    className="parent-display-input"
                    autoFocus
                  />
                  <div className="parent-edit-actions">
                    <button
                      onClick={handleUpdateDisplayName}
                      disabled={saving}
                      className="parent-edit-btn parent-edit-btn--primary"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingDisplayName(false);
                        setNewDisplayName(profile.display_name || "");
                        setError("");
                      }}
                      className="parent-edit-btn parent-edit-btn--ghost"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="parent-display-row">
                  <span className="parent-display-text">
                    {profile.display_name || profile.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingDisplayName(true)}
                    className={`parent-edit-link ${showNudge ? "parent-edit-link--nudge" : ""}`}
                    title="Personalize your display name"
                  >
                    {showNudge ? "Click to change your display name" : "Edit display name"}
                    {showNudge && (
                      <span className="nudge-arrow">
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">Email</span>
            <span className="parent-profile-value parent-profile-value--mono">
              {profile.email}
            </span>
          </div>

          {/* Role */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">Role</span>
            <span className="parent-profile-value">Parent</span>
          </div>

          {/* School */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">School</span>
            <span className="parent-profile-value">
              {profile.school_name || "No school assigned"}
            </span>
          </div>

          {/* School hashtag */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">School hashtag</span>
            <span className="parent-profile-value parent-profile-value--accent">
              {profile.school_hashtag || "Not set yet"}
            </span>
          </div>

          {/* Principal */}
          <div className="parent-profile-field">
            <span className="parent-profile-label">School principal</span>
            <span className="parent-profile-value">
              {profile.principal_name || "Not available"}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParentProfile;